import { ReactNode, useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UserItem } from '@/types/userItem';
import { useQuickListItem } from '@/hooks/useQuickListItem';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { resolveCollectrPriceCents } from '@/lib/collectrPricing';
import { useIsMobile } from '@/hooks/use-mobile';
import { ListingLocationWarning } from '@/components/inventory/ListingLocationWarning';
import { PhotoRequiredHint } from '@/components/inventory/PhotoRequiredHint';
import {
  useKeyboardAwareSheetStyle,
  useKeyboardAwareSheetChrome,
} from '@/hooks/useVisualViewportInset';

interface QuickListPopoverProps {
  item: UserItem;
  /** Element the popover anchors to. */
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  onListed?: () => void;
}

/**
 * One-shot listing surface.
 * Desktop: anchor-attached popover (Enter to list · Esc to cancel · O toggles offers).
 * Mobile: bottom sheet (grabbable, swipe-down to dismiss) — matches mobile-vs-desktop
 * distinct-surfaces rule.
 * Defaults "Open to offers" ON for unlisted items to match QuickPriceEditDialog v2.
 */
export function QuickListPopover({
  item,
  children,
  align = 'end',
  side = 'bottom',
  onListed,
}: QuickListPopoverProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [acceptsOffers, setAcceptsOffers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { listItem, hasLocation, profileLoaded } = useQuickListItem();

  // Prefill each time the surface opens: existing asking price → target → Collectr.
  useEffect(() => {
    if (!open) return;
    const collectr = resolveCollectrPriceCents(item);
    const seedCents =
      item.asking_price_cents ??
      item.target_price_cents ??
      collectr ??
      null;
    setPrice(seedCents ? (seedCents / 100).toFixed(2) : '');
    setTitle(item.title ?? '');
    const isAlreadyListed = item.visibility === 'listed';
    setAcceptsOffers(isAlreadyListed ? (item.is_negotiable ?? true) : true);
    // Autofocus on desktop only — mobile keyboard is intrusive here.
    if (!isMobile) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, item.id, item.asking_price_cents, item.target_price_cents, item.is_negotiable, item.title, isMobile]);

  const parsed = price ? parseFloat(price) : NaN;
  const priceCents = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : null;
  const trimmedTitle = title.trim();
  const hasTitle = trimmedTitle.length > 0;
  // Don't gate on hasLocation here — listItem() refetches the profile on submit
  // to avoid a hydration race that would leave the button permanently disabled.
  const missingLocation = profileLoaded && !hasLocation;
  const canSubmit =
    (priceCents !== null || acceptsOffers) && hasTitle && !missingLocation && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const ok = await listItem(item, {
      priceCents,
      acceptsOffers,
      title: trimmedTitle !== (item.title ?? '').trim() ? trimmedTitle : undefined,
    });
    setSubmitting(false);
    if (ok) {
      setOpen(false);
      onListed?.();
    }
  };

  const body = (
    <>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5">
          List for sale
        </div>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Item name"
          aria-label="Item name"
          className={cn(
            'w-full rounded-lg border border-white/[0.08] bg-white/[0.04] focus-visible:border-white/[0.16] focus-visible:ring-0 focus-visible:ring-offset-0',
            isMobile ? 'h-12 text-base' : 'h-10 text-[14px]',
          )}
        />
      </div>

      <div
        className={cn(
          'flex items-center gap-1 rounded-lg border border-white/[0.08] bg-white/[0.04] pl-3 pr-1 focus-within:border-white/[0.16]',
          isMobile ? 'h-12' : 'h-10',
        )}
      >
        <span className="text-muted-foreground text-[15px]">$</span>
        <Input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          pattern="[0-9]*\.?[0-9]*"
          {...PRICE_INPUT_PROPS}
          placeholder="0.00"
          value={price}
          onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (!isMobile && (e.key === 'o' || e.key === 'O')) {
              if (!price) {
                e.preventDefault();
                setAcceptsOffers((v) => !v);
              }
            }
          }}
          className={cn(
            'h-full flex-1 !bg-transparent !border-0 shadow-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0',
            isMobile ? 'text-base' : 'text-[15px]',
          )}
        />
      </div>

      <button
        type="button"
        onClick={() => setAcceptsOffers((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2.5 rounded-lg border px-3 transition-colors text-left',
          isMobile ? 'h-12' : 'h-9',
          acceptsOffers
            ? 'bg-active-gold/10 border-active-gold/40 text-active-gold'
            : 'bg-white/[0.04] border-white/[0.08] text-muted-foreground hover:text-foreground hover:bg-white/[0.06]',
        )}
      >
        <span
          className={cn(
            'w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0',
            acceptsOffers ? 'bg-active-gold border-active-gold' : 'border-muted-foreground/50',
          )}
        >
          {acceptsOffers && <Check className="w-2 h-2 text-background" strokeWidth={3} />}
        </span>
        <span className={cn('font-medium', isMobile ? 'text-sm' : 'text-[13px]')}>Open to offers</span>
      </button>

      <PhotoRequiredHint item={item} />
      <ListingLocationWarning profileLoaded={profileLoaded} hasLocation={hasLocation} />


      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          className={cn('flex-1 rounded-full text-muted-foreground hover:text-foreground', isMobile ? 'h-11' : 'h-9')}
          onClick={() => setOpen(false)}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          className={cn('flex-1 rounded-full', isMobile ? 'h-11' : 'h-9')}
          onClick={submit}
          disabled={!canSubmit}
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'List'}
        </Button>
      </div>

      {!isMobile && (
        <p className="text-[10px] text-muted-foreground/70 text-center">
          Enter to list · Esc to cancel
        </p>
      )}
    </>
  );

  if (isMobile) {
    return (
      <MobileQuickListSheet open={open} onOpenChange={setOpen} trigger={children}>
        {body}
      </MobileQuickListSheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={8}
        className="w-[260px] p-3 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}

function MobileQuickListSheet({
  open,
  onOpenChange,
  trigger,
  children,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
}) {
  const kbStyle = useKeyboardAwareSheetStyle();
  const kbChrome = useKeyboardAwareSheetChrome();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="bottom"
        {...kbChrome}
        style={kbStyle}
        className="bg-[#121416] border-t border-white/[0.06] px-5 pt-5 pb-5"
        onClick={(e) => e.stopPropagation()}
      >
        <SheetHeader className="sr-only p-0">
          <SheetTitle>List for sale</SheetTitle>
        </SheetHeader>
        <div className="space-y-2.5">{children}</div>
      </SheetContent>
    </Sheet>
  );
}
